import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Card, CardContent, IconButton, Paper } from '@mui/material';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import { isEmpty, map } from 'lodash';
import moment from 'moment';

const HorizontalList = (props) => {
    const [firstLoad, setFirstLoad] = useState(true);
    const { data, clickHandler } = props
    const [daysToShow, setDaysToShow] = useState([]);
    const scrollRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const checkScroll = () => {
        const el = scrollRef.current;
        if (el) {
            const scrollLeft = el.scrollLeft;
            const clientWidth = el.clientWidth;
            const scrollWidth = el.scrollWidth;
            
            setCanScrollLeft(scrollLeft > 0);
            setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1); // -1 for floating point precision
        }
    };

    useLayoutEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        // Check scroll immediately after mount
        checkScroll();

        // Listen for scroll changes
        el.addEventListener('scroll', checkScroll);

        // Listen for resize (e.g., window resize or content size change)
        const resizeObserver = new ResizeObserver(checkScroll);
        resizeObserver.observe(el);

        // Also check on window resize
        const handleWindowResize = () => {
            setTimeout(checkScroll, 100); // Small delay to ensure layout is complete
        };
        window.addEventListener('resize', handleWindowResize);

        return () => {
            el.removeEventListener('scroll', checkScroll);
            resizeObserver.disconnect();
            window.removeEventListener('resize', handleWindowResize);
        };
    }, []);

    useEffect(() => {
        if (scrollRef.current && firstLoad) {
            const selectedCard = scrollRef.current.querySelector('.cursor-pointer');
            if (selectedCard) {
                selectedCard.scrollIntoView({
                    behavior: 'smooth',
                    inline: 'center',
                    block: 'nearest',
                });
            }
        }
    }, [firstLoad, data]);

    const scroll = (direction) => {
        const el = scrollRef.current;
        if (el) {
            const scrollAmount = Math.min(300, el.clientWidth * 0.8); // Scroll 80% of container width or 300px, whichever is smaller
            el.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth',
            });
        }
    };

    useEffect(() => {
        if (!isEmpty(data)) {
            const dateArray = data.map(item => item.date);

            setDaysToShow(map(dateArray, (dte, index) => {
                const mmt = moment(dte, "YYYY-MM-DD")
                return {
                    index,
                    title: <div className='size-16-20 mb-16'>{mmt.format("ddd")}</div>,
                    body: <div className='size-16-24'>{mmt.format("D")}</div>,
                    description: <div className='size-14-20'>{mmt.format("MMM")}</div>,
                    date: dte,
                    available: data.some(item => item.date === dte),
                    selected: data.some(item => item.date === dte && item.selected),
                    disabled: mmt.isBefore(moment(), 'day'), // Disable past dates
                }
            }));
        }
    }, [data])

    if (isEmpty(data)) {
        return <Paper elevation={0} sx={{ p: 2, textAlign: 'center' }}>No available dates</Paper>;
    }

    return (
        <Box sx={{ position: 'relative', width: '100%' }}>
            {/* Left Arrow - positioned outside scroll container */}
            {canScrollLeft && (
                <IconButton
                    onClick={() => scroll('left')}
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '-40px',
                        transform: 'translateY(-50%)',
                        zIndex: 2,
                        color: 'black',
                        background: 'white',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        '&:hover': { 
                            background: 'white',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)' 
                        },
                        width: '32px',
                        height: '32px',
                    }}
                >
                    <ArrowBackIos fontSize='small' />
                </IconButton>
            )}

            {/* Scrollable container */}
            <Box
                ref={scrollRef}
                sx={{
                    display: 'flex',
                    overflowX: 'auto',
                    gap: 0,
                    scrollBehavior: 'smooth',
                    '&::-webkit-scrollbar': { display: 'none' },
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none', // IE and Edge
                    padding: '0 8px', // Add some padding to prevent cards from touching edges
                    width: '100%',
                    // Remove justifyContent center as it can interfere with scrolling
                }}
            >
                {/* Scrollable row content */}
                {daysToShow.map((item, index) => (
                    <Card 
                        elevation={0}
                        key={"card" + index}
                        sx={{ 
                            minWidth: 60, // Ensure minimum width for touch targets
                            flex: '0 0 auto', 
                            boxShadow: 'none',
                            margin: '0 4px', // Add spacing between cards
                        }}
                        onClick={() => {
                            setFirstLoad(false);
                            if (clickHandler && !item.disabled) {
                                clickHandler(item);
                            }
                        }}
                    >
                        <CardContent 
                            className={item.disabled ? 'LightGray' : item.available ? 'cursor-pointer' : ''}
                            sx={{ padding: '8px !important' }} // Override default padding
                        >
                            <div className={item.selected ? 'BluePrimary' : ''}>{item.title}</div>
                            <div className={item.selected ? 'bg-accent contentInversePrimary br-rd-24 p-16' : 'p-16'}>
                                <div>{item.body}</div>
                                <div>{item.description}</div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </Box>

            {/* Right Arrow - positioned outside scroll container */}
            {canScrollRight && (
                <IconButton
                    onClick={() => scroll('right')}
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        right: '-40px',
                        transform: 'translateY(-50%)',
                        zIndex: 2,
                        color: 'black',
                        background: 'white',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        '&:hover': { 
                            background: 'white',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)' 
                        },
                        width: '32px',
                        height: '32px',
                    }}
                >
                    <ArrowForwardIos fontSize='small' />
                </IconButton>
            )}
        </Box>
    );
};

export default HorizontalList;
